import type { UserBrief } from "../../types/projects";

interface Props {
  user: UserBrief | null | undefined;
  /** Fallback when the brief is null (user deleted, system row, etc). */
  fallback?: string;
  /** Optional CSS class for callers that want to style the wrapper. */
  className?: string;
  testId?: string;
}

/**
 * Display name renderer with consistent fallbacks.
 *
 * Order: ``Given Family`` → ``Given`` → ``Family`` → ``email`` →
 * ``fallback`` (default "Unknown"). This is the single chokepoint for
 * "who did this thing" displays across the app — anywhere a route
 * returns a numeric ``user_id`` directly, we instead resolve to a
 * ``UserBrief`` server-side and render through here.
 */
export function UserName({
  user,
  fallback = "Unknown",
  className,
  testId,
}: Props) {
  return (
    <span className={className} data-testid={testId} title={user?.email}>
      {formatUserName(user, fallback)}
    </span>
  );
}

/**
 * Treat a value as "missing" if it's empty OR looks like an email
 * address. Defensive against a Cognito attribute-mapping misconfig
 * where ``family_name`` ends up containing the user's email, which
 * would otherwise produce displays like
 * ``Giovanni gvanore@sunwave.digital`` (Stage 8.6 Item 6 bug).
 */
function isUsableNamePart(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.includes("@")) return false;
  return true;
}

export function formatUserName(
  user: UserBrief | null | undefined,
  fallback = "Unknown",
): string {
  if (!user) return fallback;
  const given = isUsableNamePart(user.given_name)
    ? user.given_name!.trim()
    : "";
  const family = isUsableNamePart(user.family_name)
    ? user.family_name!.trim()
    : "";
  if (given && family) return `${given} ${family}`;
  if (given) return given;
  if (family) return family;
  if (user.email) return user.email;
  return fallback;
}
